<!-- Resposta a um convite para entrar no círculo de um titular.

     Aceitar grava o consentimento DESTA pessoa no vínculo (texto abaixo, com o
     IP e o navegador do aceite), por isso o texto fica à vista e o botão só
     libera depois do "Li e aceito". Recusar não pede confirmação: nada muda na
     conta e quem convidou pode convidar de novo.

     Sem vaga no plano, o backend recusa o aceite (409) e avisa quem convidou;
     a mensagem dele aparece aqui e o convite continua valendo. -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useNotificationStore } from "@/stores/useNotificationStore";
import {
  PRIVACY_VERSION,
  TERMS_VERSION,
  buildInviteAcceptConsent,
} from "@/utils/memberValidation";
import { asUtc, formatDate } from "@/utils/dateUtils";
import { splitBold } from "@/utils/stringUtils";

const props = defineProps({
  notification: { type: Object, required: true },
});

const emit = defineEmits(["close"]);

const store = useNotificationStore();

const agreed = ref(false);
const isBusy = ref(false);
const errorMsg = ref("");
// accepted | declined, depois da resposta
const outcome = ref(null);
const acceptedName = ref("");

const circleName = computed(() => props.notification.managerName || "este círculo");
const relationshipType = computed(() => props.notification.relationshipType || "membro");
const consentText = computed(() =>
  buildInviteAcceptConsent(circleName.value, relationshipType.value)
);

async function accept() {
  if (!agreed.value || isBusy.value) return;
  isBusy.value = true;
  errorMsg.value = "";
  try {
    const result = await store.accept(props.notification.invitationId, {
      consentTextSnapshot: consentText.value,
      termsVersion: TERMS_VERSION,
      privacyVersion: PRIVACY_VERSION,
    });
    acceptedName.value = result.managerName;
    outcome.value = "accepted";
  } catch (err) {
    errorMsg.value =
      err.response?.data?.message || "Não foi possível aceitar o convite.";
    store.fetch();
  } finally {
    isBusy.value = false;
  }
}

async function decline() {
  if (isBusy.value) return;
  isBusy.value = true;
  errorMsg.value = "";
  try {
    await store.decline(props.notification.invitationId);
    outcome.value = "declined";
  } catch (err) {
    errorMsg.value =
      err.response?.data?.message || "Não foi possível recusar o convite.";
    store.fetch();
  } finally {
    isBusy.value = false;
  }
}

function close() {
  if (!isBusy.value) emit("close");
}

function onEsc(event) {
  if (event.key === "Escape") close();
}
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));
</script>

<template>
  <Teleport to="body">
    <div class="modal-backdrop invite-backdrop" @click.self="close">
      <div class="modal-panel small" role="dialog" aria-labelledby="invite-title">
        <div class="modal-header">
          <h3 id="invite-title" class="modal-title">Convite para participar</h3>
          <button
            type="button"
            class="modal-close-btn"
            :disabled="isBusy"
            aria-label="Fechar"
            @click="close"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <!-- Depois da resposta -->
        <div v-if="outcome" class="modal-body invite-done">
          <i
            class="fas invite-done-icon"
            :class="
              outcome === 'accepted'
                ? 'fa-check-circle invite-done-icon--ok'
                : 'fa-times-circle invite-done-icon--neutral'
            "
            aria-hidden="true"
          ></i>
          <template v-if="outcome === 'accepted'">
            <p class="invite-done-title">Você agora é membro de {{ acceptedName }}.</p>
            <p class="invite-done-text">
              Na próxima vez que entrar no Litterae, você vai poder escolher
              entrar como membro de {{ acceptedName }} ou na sua própria conta.
            </p>
          </template>
          <template v-else>
            <p class="invite-done-title">Convite recusado.</p>
            <p class="invite-done-text">
              Nada mudou na sua conta. Quem convidou fica sabendo e pode
              convidar de novo.
            </p>
          </template>
        </div>

        <div v-else class="modal-body">
          <p class="invite-lead">
            <template v-for="(part, i) in splitBold(notification.message)" :key="i"
              ><strong v-if="part.bold">{{ part.text }}</strong
              ><template v-else>{{ part.text }}</template></template
            >
          </p>

          <dl class="invite-facts">
            <div>
              <dt>Círculo</dt>
              <dd>{{ circleName }}</dd>
            </div>
            <div>
              <dt>Como</dt>
              <dd>{{ relationshipType }}</dd>
            </div>
            <div v-if="notification.expiresAt">
              <dt>Vale até</dt>
              <dd>{{ formatDate(asUtc(notification.expiresAt)) }}</dd>
            </div>
          </dl>

          <div class="invite-consent">{{ consentText }}</div>

          <label class="invite-agree">
            <input v-model="agreed" type="checkbox" class="checkbox-style" :disabled="isBusy" />
            <span>Li e aceito.</span>
          </label>

          <p v-if="errorMsg" class="invite-error" role="alert">{{ errorMsg }}</p>
        </div>

        <div class="modal-footer">
          <button
            v-if="outcome"
            type="button"
            class="btn btn-primary"
            @click="close"
          >
            Fechar
          </button>
          <template v-else>
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isBusy"
              @click="decline"
            >
              Recusar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!agreed || isBusy"
              @click="accept"
            >
              {{ isBusy ? "Enviando..." : "Aceitar convite" }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Abre a partir do sino, que é um popover: fica acima dele. */
.invite-backdrop {
  z-index: calc(var(--z-popover) + 1);
}

.invite-lead {
  margin: 0;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--text-dark);
}

.invite-facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3) var(--space-6);
  margin: 0;
}
.invite-facts dt {
  font-size: var(--text-xs);
  color: var(--text-light);
}
.invite-facts dd {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--text-dark);
}

.invite-consent {
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  background: var(--surface-2);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-dark);
}

.invite-agree {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-dark);
  cursor: pointer;
}

.invite-error {
  margin: 0;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  background: var(--danger-bg);
  color: var(--danger-text);
  font-size: var(--text-sm);
}

.invite-done {
  align-items: center;
  text-align: center;
}
.invite-done-icon {
  font-size: 2.5rem;
}
.invite-done-icon--ok {
  color: var(--success-text);
}
.invite-done-icon--neutral {
  color: var(--text-light);
}
.invite-done-title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: var(--text-dark);
}
.invite-done-text {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-subtle);
}
</style>
